// Pure maths behind useDashboardStats, kept free of Supabase so it can be tested.
//
// SEQ-240: the hook used to read claims_progress unpaged (1,000 of 1,494 lines)
// and never linked the schedule lines of a "custom amount only" category to
// its project, so those lines ignored the project status filter.

export interface CategoryWithSubcategories {
  project_id: string;
  is_custom_amount_only?: boolean | null;
  custom_amount?: number | string | null;
  subcategories?: { id: string; amount: number | string | null }[] | null;
}

// Contract value and the subcategory to project map for the dashboard.
//
// A category set to "custom amount only" contributes its set amount to the
// contract value and ignores its schedule lines for money, exactly like
// useVariationsStats. Its lines are still linked to the project: a claim line
// typed against one of them belongs to that project, so the status, builder
// and project filters must reach it.
export function buildContractAndSubcatMap(categories: CategoryWithSubcategories[]): {
  totalContractValue: number;
  subcatToProject: Record<string, string>;
} {
  let totalContractValue = 0;
  const subcatToProject: Record<string, string> = {};
  for (const cat of categories) {
    for (const sub of cat.subcategories || []) {
      subcatToProject[sub.id] = cat.project_id;
    }
    if (cat.is_custom_amount_only) {
      totalContractValue += Number(cat.custom_amount || 0);
    } else {
      for (const sub of cat.subcategories || []) {
        totalContractValue += Number(sub.amount || 0);
      }
    }
  }
  return { totalContractValue, subcatToProject };
}

export interface ClaimLineRow {
  amount: number | string | null;
  claim_month: number;
  claim_year: number;
}

export interface MonthlyBucket {
  month: number;
  monthName: string;
  amount: number;
}

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Period totals over EVERY claim line. A caller that hands this a truncated
// list gets a truncated total, so the caller must read all rows (fetchAllRows).
export function summariseClaimLines(
  rows: ClaimLineRow[],
  now: { month: number; year: number },
): {
  thisMonthClaims: number;
  lastMonthClaims: number;
  totalClaimsToDate: number;
  yearToDateClaims: number;
  monthlyBreakdown: MonthlyBucket[];
} {
  const lastMonth = now.month === 1 ? 12 : now.month - 1;
  const lastMonthYear = now.month === 1 ? now.year - 1 : now.year;
  const monthlyBreakdown: MonthlyBucket[] = MONTH_NAMES.map((monthName, i) => ({
    month: i + 1,
    monthName,
    amount: 0,
  }));
  let thisMonthClaims = 0;
  let lastMonthClaims = 0;
  let totalClaimsToDate = 0;
  let yearToDateClaims = 0;

  for (const row of rows) {
    const amount = Number(row.amount || 0);
    totalClaimsToDate += amount;
    if (row.claim_month === now.month && row.claim_year === now.year) thisMonthClaims += amount;
    if (row.claim_month === lastMonth && row.claim_year === lastMonthYear) lastMonthClaims += amount;
    if (row.claim_year === now.year && row.claim_month <= now.month) yearToDateClaims += amount;
    if (row.claim_year === now.year) {
      const bucket = monthlyBreakdown[row.claim_month - 1];
      if (bucket) bucket.amount += amount;
    }
  }

  return { thisMonthClaims, lastMonthClaims, totalClaimsToDate, yearToDateClaims, monthlyBreakdown };
}
